let clarityInitialized = false;

export type ClarityFunnelName = 'trial_auth' | 'bot_create';

export interface ClarityFunnelTagParams {
  funnel_source?: string;
  cta_location?: string;
  organization_id?: string;
  bot_id?: string;
  error_type?: string;
  trigger?: string;
  has_email_param?: boolean;
  scrape_failed?: boolean;
}

type ClarityFn = (...args: unknown[]) => void;

const isClarityConfigured = (): boolean => (
  typeof window !== 'undefined'
  && !!process.env.NEXT_PUBLIC_CLARITY_PROJECT_ID
  && process.env.NEXT_PUBLIC_NODE_ENV === 'production'
);

const getClarity = (): ClarityFn | undefined => {
  if (!isClarityConfigured()) {
    return undefined;
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const clarityFn = (window as any).clarity;
  return typeof clarityFn === 'function' ? clarityFn : undefined;
};

const callClarity = (...args: unknown[]): void => {
  try {
    getClarity()?.(...args);
  } catch (error) {
    console.warn('Clarity call failed:', error);
  }
};

/** Set a single custom tag — filter recordings in Clarity dashboard via Custom tags. */
export const setClarityTag = (key: string, value: string): void => {
  if (!value) {
    return;
  }
  callClarity('set', key, value);
};

/** Associate session with org id (no PII). Helps group trial funnel recordings. */
export const identifyClarityOrganization = (organizationId: string): void => {
  if (!organizationId) {
    return;
  }
  callClarity('identify', organizationId);
  setClarityTag('organization_id', organizationId);
};

/**
 * Tag the current session with funnel + step for Clarity filters.
 * Example filters: funnel = trial_auth, funnel_step = otp_submitted
 */
export const setClarityFunnelStep = (
  funnel: ClarityFunnelName,
  step: string,
  params?: ClarityFunnelTagParams,
): void => {
  setClarityTag('funnel', funnel);
  setClarityTag('funnel_step', step);

  if (params?.funnel_source) {
    setClarityTag('funnel_source', params.funnel_source);
  }
  if (params?.cta_location) {
    setClarityTag('cta_location', params.cta_location);
  }
  if (params?.error_type) {
    setClarityTag('error_type', params.error_type);
  }
  if (params?.trigger) {
    setClarityTag('discover_trigger', params.trigger);
  }
  if (params?.bot_id) {
    setClarityTag('bot_id', params.bot_id);
  }
  if (params?.has_email_param !== undefined) {
    setClarityTag('has_email_param', params.has_email_param ? 'true' : 'false');
  }
  if (params?.scrape_failed !== undefined) {
    setClarityTag('scrape_failed', params.scrape_failed ? 'true' : 'false');
  }
  if (params?.organization_id) {
    identifyClarityOrganization(params.organization_id);
  }
};

const FUNNEL_PAGE_BY_PATH: Record<string, string> = {
  '/confirmation': 'confirmation',
  '/login': 'login',
  '/bot/create': 'bot_create',
};

/** Tag session when user lands on a trial-critical route (SPA navigation). */
export const syncClarityFunnelPage = (pathname: string): void => {
  const directMatch = FUNNEL_PAGE_BY_PATH[pathname];
  if (directMatch) {
    setClarityTag('funnel_page', directMatch);
    return;
  }

  if (/^\/bot\/[^/]+\/onboarding/.test(pathname)) {
    setClarityTag('funnel_page', 'bot_onboarding');
    return;
  }

  if (/^\/bot\/[^/]+\/training\/test/.test(pathname)) {
    setClarityTag('funnel_page', 'bot_test');
  }
};

/**
 * Load Microsoft Clarity session recordings and heatmaps.
 * Requires NEXT_PUBLIC_CLARITY_PROJECT_ID; disabled outside production.
 */
export const initializeClarity = (): void => {
  if (typeof window === 'undefined' || clarityInitialized) {
    return;
  }

  const projectId = process.env.NEXT_PUBLIC_CLARITY_PROJECT_ID;
  if (!projectId || process.env.NEXT_PUBLIC_NODE_ENV !== 'production') {
    return;
  }

  try {
    // Official Clarity snippet — https://clarity.microsoft.com/
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (function claritySnippet(c: any, l: Document, a: string, r: string, i: string) {
      c[a] = c[a] || function clarityQueue(...args: unknown[]) {
        (c[a].q = c[a].q || []).push(args);
      };
      const t = l.createElement(r) as HTMLScriptElement;
      t.async = true;
      t.src = `https://www.clarity.ms/tag/${i}`;
      const y = l.getElementsByTagName(r)[0];
      y.parentNode?.insertBefore(t, y);
    }(window, document, 'clarity', 'script', projectId));
    clarityInitialized = true;
  } catch (error) {
    console.warn('Clarity initialization failed:', error);
  }
};
